import { jsx, jsxs } from 'react/jsx-runtime';
import * as React from 'react';
import { h as EGDSSheetTransition, e as EGDSSheet, f as EGDSSheetContent } from '../sheet-footer/index-chunk.esm.js';
import { c as classNames, d as EGDSContextProvider } from '../index_orig/index-chunk.esm.js';
import styles from '../styles/uitk-egds-dialog.css';
import '../button/index-chunk.esm.js';
import { E as EGDSPrimaryButton } from '../primary-button/index-chunk.esm.js';
import { E as EGDSTertiaryButton } from '../tertiary-button/index-chunk.esm.js';
import '../icon/index-chunk.esm.js';

const getClassNameList$3 = classNames.bind(styles);
var EGDSDialogAnimationType;
(function (EGDSDialogAnimationType) {
    EGDSDialogAnimationType["NONE"] = "none";
    EGDSDialogAnimationType["FADE"] = "fade";
    EGDSDialogAnimationType["SLIDEDOWN"] = "slidedown";
    EGDSDialogAnimationType["SLIDELEFT"] = "slideleft";
    EGDSDialogAnimationType["SLIDERIGHT"] = "slideright";
    EGDSDialogAnimationType["SLIDEUP"] = "slideup";
})(EGDSDialogAnimationType || (EGDSDialogAnimationType = {}));
class EGDSDialog extends React.Component {
    static displayName = 'EGDSDialog';
    static defaultProps = {
        animation: EGDSDialogAnimationType.NONE,
        autoFocus: true,
        heightAuto: false,
        returnFocusOnClose: false,
        strictFullscreen: false,
    };
    iphoneSafariUARegex = /iphone.*safari|safari.*iphone/gi;
    constructor(props) {
        super(props);
        this.setDialogHeightForSafariOnIphone = this.setDialogHeightForSafariOnIphone.bind(this);
        this.setHeight = this.setHeight.bind(this);
        this.detectIphoneSafariPortrait = this.detectIphoneSafariPortrait.bind(this);
    }
    componentDidMount() {
        this.detectIphoneSafariPortrait() && this.setDialogHeightForSafariOnIphone();
    }
    componentWillUnmount() {
        window.removeEventListener('resize', this.setHeight);
    }
    setHeight() {
        document.documentElement.style.setProperty('--vh', `${window.innerHeight}px`);
    }
    setDialogHeightForSafariOnIphone() {
        this.setHeight();
        window.addEventListener('resize', this.setHeight);
    }
    detectIphoneSafariPortrait() {
        return (typeof window !== 'undefined' &&
            typeof navigator !== 'undefined' &&
            !!navigator.userAgent.match(this.iphoneSafariUARegex) &&
            typeof window.matchMedia === 'function' &&
            window.matchMedia('(orientation: portrait)').matches);
    }
    render() {
        const { ariaLabel, autoFocus, children, className, closeCallback, dialogShow, heightAuto, openCallback, returnFocusOnClose, strictFullscreen, triggerRef, sheetType = 'centered', } = this.props;
        const defaultRef = React.createRef(); // Adding default Ref in case there's none provided
        if (!children) {
            return null;
        }
        const wrapperClassNames = getClassNameList$3('uitk-dialog', 'uitk-dialog-sheet', className, {
            'uitk-dialog-fullscreen-strict': strictFullscreen,
            'uitk-dialog-height-auto': heightAuto,
        });
        return (jsx(EGDSContextProvider, { config: { context: 'uitk-dialog-context' }, children: jsx(EGDSSheetTransition, { isVisible: dialogShow, openCallback: openCallback, closeCallback: closeCallback, children: jsx(EGDSSheet, { autoFocus: autoFocus, returnFocusOnClose: returnFocusOnClose, isVisible: dialogShow, type: sheetType, triggerRef: triggerRef || defaultRef, className: wrapperClassNames, ariaLabel: ariaLabel, centeredSheetSize: "small", children: children }) }) }));
    }
}

const getClassNameList$2 = classNames.bind(styles);
class EGDSDialogContent extends React.PureComponent {
    static displayName = 'EGDSDialogContent';
    render() {
        const { children, className, contentDomRef, zeroPadding } = this.props;
        const wrapperClassNames = getClassNameList$2('uitk-dialog-content', className, {
            'default-padding': !zeroPadding,
        });
        return (jsx(EGDSSheetContent, { className: wrapperClassNames, padded: !zeroPadding, contentDomRef: contentDomRef, children: children }));
    }
}

const getClassNameList$1 = classNames.bind(styles);
class EGDSDialogFooter extends React.Component {
    static displayName = 'EGDSDialogFooter';
    static defaultProps = {
        stacked: false,
    };
    static footerButtonWrapperClassNames = 'uitk-dialog-footer-button';
    validDialogFooterButtonTypes = (element) => !!element.type.displayName?.match(/(EGDS|Uitk)(Primary|Tertiary)Button/);
    applyFooterButtonClasses(element) {
        return React.cloneElement(element, {
            ...element.props,
            className: getClassNameList$1(element.props.className, EGDSDialogFooter.footerButtonWrapperClassNames),
        });
    }
    render() {
        const { buttonOne, buttonTwo, buttonThree, stacked, disabled, children } = this.props;
        const Tag1 = buttonOne && buttonOne.type === 'primary' ? EGDSPrimaryButton : EGDSTertiaryButton;
        const Tag2 = buttonTwo && buttonTwo.type === 'primary' ? EGDSPrimaryButton : EGDSTertiaryButton;
        const Tag3 = buttonThree && buttonThree.type === 'primary' ? EGDSPrimaryButton : EGDSTertiaryButton;
        const footerWrapperClassName = getClassNameList$1('uitk-dialog-footer', {
            'uitk-dialog-footer-stacked': stacked,
        }, {
            'is-disabled': disabled,
        });
        const footerSectionWrapperClassNames = getClassNameList$1('uitk-dialog-footer-section');
        const childrenToRender = children &&
            React.Children.toArray(children)
                .filter(this.validDialogFooterButtonTypes)
                .slice(0, stacked ? 3 : 2)
                .map(this.applyFooterButtonClasses);
        if (childrenToRender) {
            return (jsx("div", { className: footerWrapperClassName, children: childrenToRender.map((child, index) => (jsx("div", { className: footerSectionWrapperClassNames, children: child }, `footer-button-${index}`))) }));
        }
        return (jsxs("div", { className: footerWrapperClassName, children: [buttonOne && (jsx("div", { className: footerSectionWrapperClassNames, children: jsx(Tag1, { disabled: buttonOne.disabled || disabled, onClick: disabled ? undefined : buttonOne.onClick, href: buttonOne.href, className: getClassNameList$1(EGDSDialogFooter.footerButtonWrapperClassNames), "data-stid": buttonOne.stid, "aria-label": buttonOne.accessibilityLabel, children: buttonOne.text }) })), buttonTwo && (jsx("div", { className: footerSectionWrapperClassNames, children: jsx(Tag2, { disabled: buttonTwo.disabled || disabled, onClick: disabled ? undefined : buttonTwo.onClick, href: buttonTwo.href, className: getClassNameList$1(EGDSDialogFooter.footerButtonWrapperClassNames), "data-stid": buttonTwo.stid, "aria-label": buttonTwo.accessibilityLabel, children: buttonTwo.text }) })), stacked && buttonThree && (jsx("div", { className: footerSectionWrapperClassNames, children: jsx(Tag3, { disabled: buttonThree.disabled || disabled, onClick: disabled ? undefined : buttonThree.onClick, href: buttonThree.href, className: getClassNameList$1(EGDSDialogFooter.footerButtonWrapperClassNames), "data-stid": buttonThree.stid, "aria-label": buttonThree.accessibilityLabel, children: buttonThree.text }) }))] }));
    }
}

const getClassNameList = classNames.bind(styles);
var EGDSFullscreenDialogTheme;
(function (EGDSFullscreenDialogTheme) {
    EGDSFullscreenDialogTheme["CANVAS"] = "uitk-dialog-fullscreen-bg-canvas";
    EGDSFullscreenDialogTheme["DARK"] = "uitk-dialog-fullscreen-bg-dark";
    EGDSFullscreenDialogTheme["DEFAULT"] = "uitk-dialog-fullscreen-bg-default";
})(EGDSFullscreenDialogTheme || (EGDSFullscreenDialogTheme = {}));
class EGDSFullscreenDialog extends React.Component {
    static displayName = 'EGDSFullscreenDialog';
    static defaultProps = {
        theme: EGDSFullscreenDialogTheme.DEFAULT,
        wideContent: false,
    };
    render() {
        const { className, wideContent, theme } = this.props;
        const wrapperClassNames = getClassNameList('uitk-dialog-fullscreen', theme, className, {
            'uitk-dialog-wide-content': wideContent,
        });
        return jsx(EGDSDialog, { ...this.props, className: wrapperClassNames });
    }
}

export { EGDSDialogAnimationType as E, EGDSDialog as a, EGDSDialogContent as b, EGDSDialogFooter as c, EGDSFullscreenDialogTheme as d, EGDSFullscreenDialog as e };
//# sourceMappingURL=index-chunk.esm.js.map
